import { Trans, useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Dialog } from '@/common/components/Dialog/Dialog';
import { Icon } from '@/common/components/Icons/Icon';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useRequestCardPin } from '@/common/integrations/menhir/employee/card/card.hooks';
import { IMenhirCard } from '@/common/integrations/menhir/employee/card/card.types';
import { StringUtils } from '@/common/utils/string';
import { notify } from '@/common/utils/toast/toast';

interface IRequestCardPinModalProps {
  cardId: IMenhirCard['id'];
  onClose: () => void;
}

export const RequestCardPinModal = ({
  cardId,
  onClose,
}: IRequestCardPinModalProps) => {
  const { t } = useTranslation();

  const { employee } = useEmployeeDetails();
  const { mutateAsync: requestPin, isLoading } = useRequestCardPin();

  const handleConfirm = async () => {
    try {
      AnalyticsService.trackCustomEvent('card_request_pin_button_clicked');
      await requestPin({ cardId: cardId });
      notify({
        type: 'success',
        message: t('dashboard.request_card_pin_modal.notification.description'),
      });
      onClose();
    } catch {}
  };

  return (
    <Dialog
      title={t('dashboard.request_card_pin_modal.title')}
      icon={<Icon icon="lock" />}
      iconBadgeColor="accentSecondary20"
      message={
        <BodyText>
          <Trans
            i18nKey="dashboard.request_card_pin_modal.description"
            values={{
              phone_number: StringUtils.formatPhoneNumber(employee.phone),
            }}
          />{' '}
          <Trans i18nKey="dashboard.request_card_pin_modal.wrong_number_disclaimer" />
        </BodyText>
      }
      primaryAction={{
        loading: isLoading,
        onPress: handleConfirm,
        children: t('dashboard.request_card_pin_modal.confirm_label'),
      }}
      secondaryAction={{
        variant: 'secondary',
        onPress: onClose,
        children: t('general.navigation.close'),
        disabled: isLoading,
      }}
      onClose={onClose}
    />
  );
};
